import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { TabItem } from '../../shared/models/tabs-models';
import { Tabs } from '../../shared/tabs/tabs';
import { OnboradingService } from '../shared/service/onboarding.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-onboard',
  standalone: true,
  imports: [RouterOutlet, Tabs, TranslatePipe],
  templateUrl: './onboard.html',
  styleUrl: './onboard.css',
})
export class Onboard implements OnInit, OnDestroy {
  crptOnbrdngSeq: number | null = null;
  clntSeq: number | null = null;

  currentChildPath = '';

  isViewMode = false;
  isTaskView = false;

  // Task se Corporate page open ho to route ka ID entySeq hoga.
  taskEntitySeq: number | null = null;

  onboardingTabs: TabItem[] = [];

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private onboarding: OnboradingService,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.router.events
        .pipe(
          filter(
            (event) =>
              event instanceof NavigationEnd,
          ),
        )
        .subscribe(() => {
          this.syncFromChild();
        }),
    );

    this.syncFromChild();

    this.subs.push(
      this.onboarding.crptOnbrdngSeq$.subscribe(
        (seq) => {
          this.crptOnbrdngSeq = seq;
          this.buildTabs();
        },
      ),
    );

    this.subs.push(
      this.onboarding.clntSeq$.subscribe(
        (seq) => {
          this.clntSeq = seq;
          this.buildTabs();
        },
      ),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((subscription) =>
      subscription.unsubscribe(),
    );

    this.onboarding.resetContext();
  }

  private syncFromChild(): void {
    let childRoute = this.route.firstChild;

    while (childRoute?.firstChild) {
      childRoute = childRoute.firstChild;
    }

    this.currentChildPath =
      childRoute?.snapshot.url[0]?.path ?? '';

    const idParam =
      childRoute?.snapshot.paramMap.get('id');

    const queryParams =
      childRoute?.snapshot.queryParamMap;

    const clientSeqQuery =
      queryParams?.get('clientSeq') ??
      queryParams?.get('clientId');

    this.isViewMode =
      queryParams?.get('mode') === 'view';

    this.isTaskView =
      queryParams?.get('fromTask') === 'true';

    if (
      this.currentChildPath === 'corporate'
    ) {
      if (this.isTaskView) {
        // Task route:
        // /onboarding/corporate/{entySeq}
        this.taskEntitySeq = idParam
          ? Number(idParam)
          : null;

        // Do not set route ID as clientSeq.
      } else {
        // Normal route:
        // /onboarding/corporate/{clientSeq}
        if (idParam) {
          this.onboarding.setClientSeq(
            Number(idParam),
          );
        }

        this.taskEntitySeq = null;
      }
    } else {
      // Other onboarding tabs use corporate onboarding sequence.
      if (idParam) {
        this.onboarding.setCorporateSeq(
          Number(idParam),
        );
      }
    }

    if (clientSeqQuery) {
      this.onboarding.setClientSeq(
        Number(clientSeqQuery),
      );
    }

    this.buildTabs();
  }

  private buildTabs(): void {
    const corporateSeq =
      this.crptOnbrdngSeq;

    const clientSeq =
      this.clntSeq;

    /*
     * Corporate tab needs separate query params.
     * From task, its URL must remain exactly:
     * /onboarding/corporate/{entySeq}?mode=view&fromTask=true
     *
     * This exact route matching keeps the first tab active.
     */
    const corporateQueryParams: any = {};

    if (this.isViewMode) {
      corporateQueryParams.mode = 'view';
    }

    if (this.isTaskView) {
      corporateQueryParams.fromTask = 'true';
    } else if (clientSeq) {
      corporateQueryParams.clientSeq =
        clientSeq;
    }

    // Other tabs use corporateSeq and clientSeq.
    const childQueryParams: any = {};

    if (clientSeq) {
      childQueryParams.clientSeq =
        clientSeq;
    }

    if (this.isViewMode) {
      childQueryParams.mode = 'view';
    }

    let corporateRoute =
      '/onboarding/corporate';

    if (
      this.isTaskView &&
      this.taskEntitySeq
    ) {
      corporateRoute =
        `/onboarding/corporate/${this.taskEntitySeq}`;
    } else if (clientSeq) {
      corporateRoute =
        `/onboarding/corporate/${clientSeq}`;
    }

    this.onboardingTabs = [
      {
        label: 'ONBOARDING.TAB_CORPORATE',
        route: corporateRoute,
        queryParams: corporateQueryParams,
      },
      {
        label: 'ONBOARDING.TAB_KYC',
        route: corporateSeq
          ? `/onboarding/risk-fatca/${corporateSeq}`
          : '',
        queryParams: childQueryParams,
        disabled: !corporateSeq,
      },
      {
        label: 'ONBOARDING.TAB_UBOS',
        route: corporateSeq
          ? `/onboarding/ubos/${corporateSeq}`
          : '',
        queryParams: childQueryParams,
        disabled: !corporateSeq,
      },
      {
        label: 'ONBOARDING.TAB_DIRECTORS',
        route: corporateSeq
          ? `/onboarding/directors/${corporateSeq}`
          : '',
        queryParams: childQueryParams,
        disabled: !corporateSeq,
      },
      {
        label: 'ONBOARDING.TAB_KEY_MANAGEMENT',
        route: corporateSeq
          ? `/onboarding/key-management/${corporateSeq}`
          : '',
        queryParams: childQueryParams,
        disabled: !corporateSeq,
      },
      {
        label: 'ONBOARDING.TAB_COMMITTEE_MEMBERS',
        route: corporateSeq
          ? `/onboarding/committee-members/${corporateSeq}`
          : '',
        queryParams: childQueryParams,
        disabled: !corporateSeq,
      },
      {
        label: 'ONBOARDING.TAB_DOCUMENTS',
        route: corporateSeq
          ? `/onboarding/documents/${corporateSeq}`
          : '',
        queryParams: childQueryParams,
        disabled: !corporateSeq,
      },
      {
        label: 'ONBOARDING.TAB_REVIEW',
        route: corporateSeq
          ? `/onboarding/review-details/${corporateSeq}`
          : '',
        queryParams: childQueryParams,
        disabled: !corporateSeq,
      },
    ];
  }
}